<template>
  <modal
    name="CategoryOrderModal"
    :width="'90%'"
    :max-width="620"
    height="auto"
    :adaptive="true"
    :scrollable="true"
    class="category-order-vmodal"
    @closed="resetState"
  >
    <section class="category-order-modal" aria-labelledby="category-order-title">
      <header class="category-order-header">
        <div>
          <h2 id="category-order-title">Definir orden de categorías</h2>
          <p>Arrastre una categoría o utilice las flechas para cambiar su posición en la herramienta de capas.</p>
        </div>
        <button type="button" class="close-button" title="Cerrar" :disabled="saving" @click="close">
          &times;
        </button>
      </header>

      <div class="category-order-body">
        <div v-if="loading" class="category-order-state">
          <font-awesome-icon icon="spinner" spin />
          <span>Cargando categorías...</span>
        </div>

        <div v-else-if="errorMessage && orderedCategories.length === 0" class="category-order-error" role="alert">
          {{ errorMessage }}
        </div>

        <div v-else-if="orderedCategories.length === 0" class="category-order-state">
          No hay categorías disponibles para ordenar.
        </div>

        <ol v-else class="category-order-list">
          <li
            v-for="(category, index) in orderedCategories"
            :key="category.id"
            :class="{ dragging: draggedIndex === index }"
            draggable="true"
            @dragstart="startDrag(index, $event)"
            @dragover.prevent
            @drop="dropCategory(index)"
            @dragend="draggedIndex = null"
          >
            <span class="category-position">{{ index + 1 }}</span>
            <span class="drag-handle" title="Arrastrar para reordenar">
              <font-awesome-icon icon="sort" />
            </span>
            <span class="category-name">{{ category.name }}</span>
            <div class="category-order-controls">
              <button
                type="button"
                title="Subir categoría"
                :disabled="index === 0 || saving"
                @click="moveCategory(index, -1)"
              >
                <font-awesome-icon icon="arrow-up" />
              </button>
              <button
                type="button"
                title="Bajar categoría"
                :disabled="index === orderedCategories.length - 1 || saving"
                @click="moveCategory(index, 1)"
              >
                <font-awesome-icon icon="arrow-down" />
              </button>
            </div>
          </li>
        </ol>

        <div v-if="errorMessage && orderedCategories.length > 0" class="category-order-error" role="alert">
          {{ errorMessage }}
        </div>
      </div>

      <footer class="category-order-footer">
        <button type="button" class="button-secondary" :disabled="saving" @click="close">
          Cancelar
        </button>
        <button
          type="button"
          class="button-primary"
          :disabled="loading || saving || orderedCategories.length === 0 || !hasChanges"
          @click="saveOrder"
        >
          <font-awesome-icon v-if="saving" icon="spinner" spin />
          {{ saving ? 'Guardando...' : 'Guardar orden' }}
        </button>
      </footer>
    </section>
  </modal>
</template>

<script>
import { restCategory } from '@/components/mixin/restCategory.js'

export default {
  name: 'CategoryOrderModal',
  mixins: [restCategory],
  data() {
    return {
      orderedCategories: [],
      originalOrder: [],
      loading: false,
      saving: false,
      errorMessage: '',
      draggedIndex: null
    }
  },
  computed: {
    hasChanges() {
      const currentOrder = this.orderedCategories.map(category => category.id)
      const orderChanged = currentOrder.some((id, index) => id !== this.originalOrder[index])
      const needsInitialization = this.orderedCategories.some(category => Number(category.orden || 0) <= 0)
      return orderChanged || needsInitialization
    }
  },
  methods: {
    show() {
      this.resetState()
      this.loading = true
      this.$modal.show('CategoryOrderModal')

      this.getCategoriesForOrder()
        .then(categories => {
          this.orderedCategories = categories.map(category => ({ ...category }))
          this.originalOrder = this.orderedCategories.map(category => category.id)
        })
        .catch(error => {
          this.errorMessage = this.getErrorMessage(error, 'No fue posible cargar las categorías.')
        })
        .then(() => {
          this.loading = false
        })
    },
    close() {
      if (!this.saving) this.$modal.hide('CategoryOrderModal')
    },
    resetState() {
      this.orderedCategories = []
      this.originalOrder = []
      this.loading = false
      this.saving = false
      this.errorMessage = ''
      this.draggedIndex = null
    },
    startDrag(index, event) {
      if (this.saving) return
      this.draggedIndex = index
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', String(index))
    },
    dropCategory(targetIndex) {
      if (this.draggedIndex === null || this.draggedIndex === targetIndex || this.saving) return
      const movedCategory = this.orderedCategories.splice(this.draggedIndex, 1)[0]
      this.orderedCategories.splice(targetIndex, 0, movedCategory)
      this.draggedIndex = null
    },
    moveCategory(index, direction) {
      const targetIndex = index + direction
      if (targetIndex < 0 || targetIndex >= this.orderedCategories.length) return
      const movedCategory = this.orderedCategories.splice(index, 1)[0]
      this.orderedCategories.splice(targetIndex, 0, movedCategory)
    },
    saveOrder() {
      if (!this.hasChanges || this.saving) return
      this.saving = true
      this.errorMessage = ''
      const categoryIds = this.orderedCategories.map(category => category.id)

      this.putCategoriesOrder(categoryIds)
        .then(() => {
          this.$emit('saved')
          this.$modal.hide('CategoryOrderModal')
        })
        .catch(error => {
          this.errorMessage = this.getErrorMessage(error, 'No fue posible guardar el orden de las categorías.')
        })
        .then(() => {
          this.saving = false
        })
    },
    getErrorMessage(error, fallback) {
      return error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : fallback
    }
  }
}
</script>

<style lang="scss" scoped>
.category-order-vmodal::v-deep .vm--modal {
  background: transparent;
  box-shadow: none;
}

.category-order-modal {
  overflow: hidden;
  width: 100%;
  max-height: 88vh;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 18px 48px rgba(15, 42, 61, 0.28);
  color: var(--color-navy);
}

.category-order-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.4rem;
  border-bottom: 1px solid #dfe6eb;
}

.category-order-header h2 {
  margin: 0 0 0.35rem;
  font-size: 1.15rem;
}

.category-order-header p {
  margin: 0;
  color: #667786;
  font-size: 0.82rem;
  line-height: 1.45;
}

.close-button {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #667786;
  font-size: 1.4rem;
  cursor: pointer;
}

.close-button:hover:not(:disabled) {
  background: #edf2f5;
  color: var(--color-navy);
}

.category-order-body {
  overflow-y: auto;
  max-height: 60vh;
  padding: 1rem 1.4rem;
  background: #f7f9fa;
}

.category-order-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  min-height: 130px;
  color: #667786;
}

.category-order-list {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.category-order-list li {
  display: grid;
  grid-template-columns: 32px 24px minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.55rem;
  min-height: 44px;
  padding: 0.45rem 0.55rem;
  border: 1px solid #d7e0e6;
  border-radius: 5px;
  background: #fff;
  cursor: grab;
}

.category-order-list li.dragging {
  opacity: 0.5;
  border-color: var(--color-orange);
}

.category-position {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  background: #edf2f5;
  color: var(--color-navy);
  font-size: 0.78rem;
  font-weight: 700;
}

.drag-handle {
  color: #8a9aa7;
  text-align: center;
}

.category-name {
  overflow: hidden;
  color: #32485a;
  font-size: 0.86rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.category-order-controls {
  display: flex;
  gap: 0.35rem;
}

.category-order-controls button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid #cbd6de;
  border-radius: 4px;
  background: #fff;
  color: var(--color-navy);
  cursor: pointer;
}

.category-order-controls button:hover:not(:disabled) {
  border-color: var(--color-orange);
  color: var(--color-orange);
}

.category-order-controls button:disabled,
.close-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.category-order-error {
  margin-top: 0.8rem;
  padding: 0.65rem 0.8rem;
  border: 1px solid #edb9b5;
  border-radius: 4px;
  background: #fff1f0;
  color: #922b21;
  font-size: 0.82rem;
}

.category-order-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  padding: 1rem 1.4rem;
  border-top: 1px solid #dfe6eb;
  background: #fff;
}

.category-order-footer button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  min-height: 36px;
  padding: 0.45rem 0.9rem;
  border-radius: 4px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
}

.button-secondary {
  border: 1px solid #cbd6de;
  background: #fff;
  color: var(--color-navy);
}

.button-primary {
  border: 1px solid var(--color-navy);
  background: var(--color-navy);
  color: #fff;
}

.category-order-footer button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 520px) {
  .category-order-list li {
    grid-template-columns: 28px 20px minmax(0, 1fr);
  }

  .category-order-controls {
    grid-column: 1 / -1;
    justify-content: flex-end;
  }
}
</style>
